import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, Palette, Share2, CheckCircle2, X } from "lucide-react";

/**
 * TenantOnboardingChecklist — persistent 3-step checklist card that lives
 * at the top of /tenant/settings until every step is complete.
 *
 * Props:
 *  - steps: { logo: bool, color: bool, share: bool }
 *  - onDismiss (): user pressed the close button (backend marks skip)
 *  - onFocusLogo (): scroll to logo uploader
 *  - onFocusColor (): scroll to color picker
 *  - onCopyUrl (): copy URL to clipboard + mark share done
 */
export default function TenantOnboardingChecklist({
  steps = { logo: false, color: false, share: false },
  onDismiss,
  onFocusLogo,
  onFocusColor,
  onCopyUrl,
}) {
  const items = [
    { key: "logo", icon: Upload, title: "Upload your logo", cta: "Choose file", action: onFocusLogo },
    { key: "color", icon: Palette, title: "Pick your brand color", cta: "Open picker", action: onFocusColor },
    { key: "share", icon: Share2, title: "Copy & share your URL", cta: "Copy URL", action: onCopyUrl },
  ];

  const done = items.filter(i => steps[i.key]).length;
  const total = items.length;
  const allDone = done === total;

  if (allDone) return null;

  return (
    <Card className="p-6 border-2 border-primary/30 bg-primary/5 relative overflow-hidden" data-testid="tenant-onboarding-checklist">
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-secondary transition-colors"
        aria-label="Dismiss onboarding"
        data-testid="tenant-onboarding-dismiss"
      >
        <X size={14} />
      </button>

      <div className="flex items-center justify-between mb-4 pr-8">
        <div>
          <div className="label-caps text-[10px] text-primary mb-1">Get your site live</div>
          <div className="font-serif text-2xl leading-tight">
            {done} of {total} steps done
          </div>
        </div>
        <div className="text-right">
          <div className="text-4xl font-serif text-primary leading-none">{Math.round((done / total) * 100)}%</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">Complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-6">
        <div
          className="h-full bg-primary transition-all duration-500 ease-out"
          style={{ width: `${(done / total) * 100}%` }}
        />
      </div>

      <ul className="space-y-2">
        {items.map((item) => {
          const complete = !!steps[item.key];
          const Icon = complete ? CheckCircle2 : item.icon;
          return (
            <li
              key={item.key}
              className={`flex items-center gap-3 rounded-md p-3 border transition-all ${
                complete
                  ? "bg-secondary/40 border-border opacity-70"
                  : "bg-background border-primary/20 onboarding-step-incomplete"
              }`}
              data-testid={`tenant-onboarding-step-${item.key}`}
              data-complete={complete}
            >
              <div className={`h-8 w-8 rounded-md grid place-items-center flex-shrink-0 ${
                complete ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"
              }`}>
                <Icon size={16} />
              </div>
              <div className={`flex-1 text-sm ${complete ? "line-through text-muted-foreground" : "font-medium"}`}>
                {item.title}
              </div>
              {!complete && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={item.action}
                  data-testid={`tenant-onboarding-cta-${item.key}`}
                >
                  {item.cta}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
